// CloudIntegration.tsx - Multi-Cloud Platform Selection
// Spec 14: Multi-Cloud Integration
// 6 platform: Cloudflare (onerilen), AWS, GCP, Azure, Google Drive, OneDrive

import { CheckCircle, Cloud, Database, HardDrive, Star, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import APIKeyModal from '../components/cloud/APIKeyModal'
import { API_URL, apiFetch } from '../config/api'

interface Platform {
  id: string
  name: string
  features: string[]
  free_tier: {
    requests?: number
    storage_gb?: number
    database_reads?: number
    database_gb?: number
    database_ru?: number
  }
  recommended: boolean
  speed: string
  cost: string
}

export default function CloudIntegration() {
  const { user } = useAuth()
  const [platforms, setPlatforms] = useState<Platform[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [connectSuccess, setConnectSuccess] = useState(false)

  useEffect(() => {
    void fetchPlatforms()
  }, [])

  const fetchPlatforms = async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/cloud/platforms`)
      const data = await response.json() as { platforms: Platform[] }
      setPlatforms(data.platforms)
    } catch (error) {
      console.error('Platform fetch error:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPlatformIcon = (platformId: string) => {
    switch (platformId) {
      case 'cloudflare':
        return <Zap size={32} className="text-orange-500" />
      case 'aws':
        return <Cloud size={32} className="text-orange-600" />
      case 'gcp':
        return <Cloud size={32} className="text-blue-500" />
      case 'azure':
        return <Cloud size={32} className="text-blue-600" />
      case 'gdrive':
        return <HardDrive size={32} className="text-green-500" />
      case 'onedrive':
        return <HardDrive size={32} className="text-blue-400" />
      default:
        return <Cloud size={32} />
    }
  }

  const getFeatureIcon = (feature: string) => {
    switch (feature) {
      case 'compute':
        return <Zap size={16} />
      case 'storage':
        return <HardDrive size={16} />
      case 'database':
        return <Database size={16} />
      default:
        return <CheckCircle size={16} />
    }
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K`
    }
    return num.toString()
  }

  const handleConnect = async (credentials: Record<string, string>) => {
    if (!selectedPlatform) {
      return
    }

    // 1. Validate credentials
    const validateResponse = await apiFetch(`${API_URL}/api/cloud/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platform: selectedPlatform,
        credentials
      })
    })

    const validateData = await validateResponse.json() as { valid: boolean; error?: string }

    if (!validateData.valid) {
      throw new Error(validateData.error || 'Dogrulama basarisiz')
    }

    // 2. Connect platform
    const connectResponse = await apiFetch(`${API_URL}/api/cloud/connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: user?.id,
        platform: selectedPlatform,
        credentials,
        connection_name: `${selectedPlatform} Connection`
      })
    })

    const connectData = await connectResponse.json() as { success: boolean; error?: string }

    if (!connectData.success) {
      throw new Error(connectData.error || 'Baglanti basarisiz')
    }

    setConnectSuccess(true)
    setTimeout(() => setConnectSuccess(false), 3000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p style={{ color: 'var(--text-secondary)' }}>Platformlar yukleniyor...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--body-bg)', padding: '20px' }}>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Multi-Cloud Integration
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          RunAI projelerinizi 6 farkli cloud platformuna deploy edin. Ucretsiz tier&apos;lardan yararlanin.
        </p>
      </div>

      {/* Platform Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {platforms.map((platform) => (
          <div
            key={platform.id}
            className="card hover:shadow-lg transition-shadow cursor-pointer"
            style={{
              background: 'var(--card-bg)',
              border: selectedPlatform === platform.id ? '2px solid var(--primary-color)' : '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '20px',
              position: 'relative'
            }}
            onClick={() => setSelectedPlatform(platform.id)}
          >
            {/* Recommended Badge */}
            {platform.recommended && (
              <div
                className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded"
                style={{ background: 'var(--warning-bg)', color: 'var(--warning-color)' }}
              >
                <Star size={14} fill="currentColor" />
                <span className="text-xs font-semibold">Onerilen</span>
              </div>
            )}

            {/* Platform Icon & Name */}
            <div className="flex items-center gap-3 mb-4">
              {getPlatformIcon(platform.id)}
              <div>
                <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {platform.name}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className="text-xs px-2 py-0.5 rounded"
                    style={{
                      background: platform.speed === 'fast' ? 'var(--success-bg)' : 'var(--info-bg)',
                      color: platform.speed === 'fast' ? 'var(--success-color)' : 'var(--info-color)'
                    }}
                  >
                    {platform.speed === 'fast' ? 'Hizli' : 'Orta'}
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded"
                    style={{
                      background: platform.cost === 'low' ? 'var(--success-bg)' : 'var(--warning-bg)',
                      color: platform.cost === 'low' ? 'var(--success-color)' : 'var(--warning-color)'
                    }}
                  >
                    {platform.cost === 'low' ? 'Dusuk Maliyet' : 'Orta Maliyet'}
                  </span>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="mb-4">
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                Ozellikler:
              </p>
              <div className="flex flex-wrap gap-2">
                {platform.features.map((feature) => (
                  <div
                    key={feature}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs"
                    style={{ background: 'var(--secondary-bg)', color: 'var(--text-primary)' }}
                  >
                    {getFeatureIcon(feature)}
                    <span className="capitalize">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Free Tier */}
            <div
              className="p-3 rounded"
              style={{ background: 'var(--secondary-bg)' }}
            >
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Ucretsiz Tier:
              </p>
              <div className="space-y-1">
                {platform.free_tier.requests && (
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>Request:</span>
                    <span style={{ color: 'var(--text-primary)' }} className="font-semibold">
                      {formatNumber(platform.free_tier.requests)}/gun
                    </span>
                  </div>
                )}
                {platform.free_tier.storage_gb && (
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>Storage:</span>
                    <span style={{ color: 'var(--text-primary)' }} className="font-semibold">
                      {platform.free_tier.storage_gb}GB
                    </span>
                  </div>
                )}
                {platform.free_tier.database_reads && (
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>DB Reads:</span>
                    <span style={{ color: 'var(--text-primary)' }} className="font-semibold">
                      {formatNumber(platform.free_tier.database_reads)}/gun
                    </span>
                  </div>
                )}
                {platform.free_tier.database_gb && (
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>Database:</span>
                    <span style={{ color: 'var(--text-primary)' }} className="font-semibold">
                      {platform.free_tier.database_gb}GB
                    </span>
                  </div>
                )}
                {platform.free_tier.database_ru && (
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--text-secondary)' }}>DB RU/s:</span>
                    <span style={{ color: 'var(--text-primary)' }} className="font-semibold">
                      {platform.free_tier.database_ru}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Connect Button */}
            <button
              className="w-full mt-4 py-2 px-4 rounded font-semibold transition-colors"
              style={{
                background: selectedPlatform === platform.id ? 'var(--primary-color)' : 'var(--secondary-bg)',
                color: selectedPlatform === platform.id ? 'white' : 'var(--text-primary)'
              }}
              onClick={(e) => {
                e.stopPropagation()
                setSelectedPlatform(platform.id)
              }}
            >
              {selectedPlatform === platform.id ? 'Secildi' : 'Sec'}
            </button>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      {selectedPlatform && (
        <div className="flex justify-center gap-4">
          <button
            className="btn btn-primary px-6 py-3 rounded font-semibold"
            style={{ background: 'var(--primary-color)', color: 'white' }}
            onClick={() => setShowModal(true)}
          >
            Baglan
          </button>
          <button
            className="btn btn-secondary px-6 py-3 rounded font-semibold"
            style={{ background: 'var(--secondary-bg)', color: 'var(--text-primary)' }}
            onClick={() => {
              // Platform karsilastirma sayfasina git (Task 14)
              console.log('Compare platforms')
            }}
          >
            Platformlari Karsilastir
          </button>
        </div>
      )}

      {/* Success Message */}
      {connectSuccess && (
        <div
          className="fixed bottom-4 right-4 flex items-center gap-2 px-4 py-3 rounded shadow-lg"
          style={{ background: 'var(--success-bg)', color: 'var(--success-color)' }}
        >
          <CheckCircle size={20} />
          <span>Platform basariyla baglandi!</span>
        </div>
      )}

      {/* API Key Modal */}
      {selectedPlatform && (
        <APIKeyModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          platform={{
            id: selectedPlatform,
            name: platforms.find(p => p.id === selectedPlatform)?.name || ''
          }}
          onConnect={handleConnect}
        />
      )}
    </div>
  )
}
